/**
 * Historical era scene — the late Ottoman Kaaba (1890s). Registered under the
 * era id `kaaba-1890s` (i18n labelKey `y1890s`, title "1890s").
 * Same shape as the other real era scenes (`kaaba2000Bce.ts` / `kaaba605.ts`):
 * STANDALONE world-scale config, child of `kaaba` on the stack, entered via the
 * slider's Explore CTA / the menu's «Historical Scenes» tab.
 *
 * Splat transform: NONE (identity) — world-scale export.
 *
 * Camera poses + colliders are hand-tuned in `?dev` (Camera, Colliders). Orbit is
 * free; the camera's floor is `clampMinHeight`, deliberately not a polar cap —
 * see the `controls` block for why.
 */
import type { SceneConfig } from '../sceneConfig';
import { resolveCdnUrl } from '../../utils/cdnUrl';

// v8 is the modeller's re-export ("final"). Measured, it is a small real win and
// not the fix: replaying the scene's own flyover in Chrome (M3, DPR 2, 1440x726,
// two runs each, alternating) gives 18.1 / 18.6 fps against v6's 16.2 / 16.9 —
// p50 50 ms against 58 ms. Read that as ~+2 fps, not more: at 120 Hz the frame
// lands on a vsync tick, so 50 vs 58 ms is one 8.3 ms tick apart and the true
// cost difference is bounded by it. Nothing structural changed — 1.99M -> 2.02M
// splats (more, not fewer), identical file sizes, and the oversized ground
// gaussians that carry ~60 % of this scene's raster area are untouched (3.2 % of
// splats with a scale axis > 0.10 in both; max axis 3.467 in both). Static
// raster area along the flyover orbit moves 1-3 %; the gain that does show up
// comes from denser, less transparent splats (median alpha 0.39 -> 0.49).
// The scene is still the heaviest era. The levers that would actually move it:
// cap splat scale in the asset, or cap desktop `pixelRatio` in qualityProfile.ts
// (v6 at DPR 1 measured 28 fps).
// Geometry held still — bbox and the cube's above-ground footprint
// x[-0.60, 0.60] y[-0.70, 0.15] z[-0.60, 0.60] match v6 — so camera poses,
// colliders, POI anchors and the v5 preloader cloud carry over untouched.
// (v7 was a local pruning experiment, never shipped.)
// The filename MUST change with the content: the CDN serves models
// `immutable, max-age=31536000`.
const KAABA_OTTOMAN_2M_URL = resolveCdnUrl('models/historical/kaaba/Kaaba_Osman_v8_2M.sog');
const KAABA_OTTOMAN_1M_URL = resolveCdnUrl('models/historical/kaaba/Kaaba_Osman_v8_1M.sog');
const KAABA_OTTOMAN_600K_URL = resolveCdnUrl('models/historical/kaaba/Kaaba_Osman_v8_600K.sog');

const TARGET_POINT = { x: 0.01, y: -0.45, z: 0.13 };

export const kaabaOttomanSceneConfig: SceneConfig = {
  id: 'kaaba-1890s',
  renderer: {
    clearColor: 0x0f0f13,
  },
  camera: {
    fov: 60,
    near: 0.01,
    far: 1e4,
    initialPosition: { x: 0.44, y: 0.26, z: 0.87 },
    previewTourStart: { x: -0.18, y: 1.46, z: 4.59 },
    previewTourEnd: {
      position: { x: -0.75, y: 0.62, z: 2.92 },
      target: TARGET_POINT,
    },
    flyover: {
      delay: 1,
      segments: [
        {
          type: 'waypoint',
          position: { x: -0.75, y: 2.355, z: 2.92 },
          target: { x: 0.01, y: -0.45, z: 0.13 },
          duration: 2,
        },
        {
          type: 'orbit',
          center: { x: 0.01, y: -0.45, z: 0.13 },
          radius: 2.892,
          height: 2.805,
          angleDeg: 360,
          duration: 8,
        },
        {
          type: 'waypoint',
          position: { x: -0.75, y: 0.62, z: 2.92 },
          target: { x: 0.01, y: -0.45, z: 0.13 },
          duration: 2,
        },
      ],
    },
  },
  // Free orbit; the camera's floor is an absolute Y clamp, not a polar cap.
  //
  // A polar cap is measured around `controls.target`, and after a POI fly-to
  // that target IS the POI. `ajyad-fortress-1890` sits 7.8 units off-centre, so
  // any framing that looks slightly UP at it is polar > 90° around the POI and
  // outside an 85° cap — while being a perfectly ordinary 36° around the scene
  // centre. Three.js then lifts the camera to the cap every frame and the
  // `scene-boundary` ceiling pushes it back, shortening the orbit radius each
  // cycle: a 1-unit snap on arrival plus an endless creep toward the POI.
  // `clampMinHeight` expresses the same intent ("don't dive under the ground")
  // in absolute Y, so it is target-independent and idempotent under Three.js'
  // spherical round-trip. Ground here is y ≈ -0.70 (the cube's footprint).
  controls: {
    orbitMinDistance: 0,
    orbitMaxDistance: 10000,
    orbitMinPolarAngle: 0,
    orbitMaxPolarAngle: Math.PI,
    constrainVertical: false,
    clampMinHeight: -0.55,
    clampMaxHeight: Infinity,
    clampMaxHeightFar: Infinity,
    orbitCenter: TARGET_POINT,
  },
  colliders: [
    {
      id: 'kaaba-cube',
      type: 'box',
      mode: 'solid',
      enabled: true,
      position: { x: -0.02682253417921851, y: -0.2068711253787996, z: 0.02490459552269486 },
      size: { x: 0.8, y: 0.8, z: 0.6 },
    },
    {
      id: 'building-1',
      type: 'box',
      mode: 'solid',
      enabled: true,
      position: { x: -0.8390320302776392, y: -0.328573533766111, z: 1.334348933714039 },
      size: { x: 0.8, y: 0.7, z: 0.6 },
      rotation: { x: 0, y: -0.6981317007977318, z: 0 },
    },
    {
      id: 'scene-boundary',
      type: 'cylinder',
      mode: 'hollow',
      enabled: true,
      blocksLineOfSight: false,
      position: TARGET_POINT,
      radius: 3.5,
      height: 3,
    },
  ],
  splat: {
    defaultByQuality: {
      desktop: 'kaaba_ottoman_2m',
      mobile: {
        high: 'kaaba_ottoman_1m',
        middle: 'kaaba_ottoman_1m',
        low: 'kaaba_ottoman_600k',
      },
    },
    // No transform → identity (world-scale export).
    variants: [
      { key: 'kaaba_ottoman_2m', url: KAABA_OTTOMAN_2M_URL },
      { key: 'kaaba_ottoman_1m', url: KAABA_OTTOMAN_1M_URL },
      { key: 'kaaba_ottoman_600k', url: KAABA_OTTOMAN_600K_URL },
    ],
    revealAnimationDuration: 2.5,
    revealDistanceScale: 0.8,
    revealMode: '3d',
    revealOrigin: TARGET_POINT,
  },
  previewModel: {
    url: resolveCdnUrl('preview/Kaaba_Osman_preloader_pnts_150000_meshopt(17).glb'),
    style: {
      pointSizeFactor: 2.5,
      wavesCenter: TARGET_POINT,
    },
  },
  assistant: {
    position: { x: 0, y: 0, z: -0.03 },
    rotationY: 0,
    scale: 0.0000625,
    reflectionResolution: 128,
  },
  environment: {
    hemisphereLight: {
      skyColor: 0xffe6c4,
      groundColor: 0x1a2027,
      intensity: 0.45,
    },
    directionalLights: [
      {
        color: 0xfff2d5,
        intensity: 0.85,
        position: { x: -0.8, y: 1.2, z: 0.6 },
      },
      {
        color: 0xbdd4ff,
        intensity: 0.35,
        position: { x: 0.9, y: 0.6, z: -0.45 },
      },
    ],
    background: {
      color: 0x0f0f13,
      skybox: {
        url: resolveCdnUrl('environment/autumn_field_puresky_8k.jpg'),
        mobileUrl: resolveCdnUrl('environment/autumn_field_puresky_2k.jpg'),
        intensity: 1.0,
        blur: 0.0,
        rotation: [0, 205, 0],
      },
    },
  },
};
